import { useMemo } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';

import { UseBaseTable } from 'hooks/useBaseTable';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import { useQueryState } from 'hooks/useQueryState';
import { useQuerystring } from 'hooks/useQuerystring';

import { useQueryGetKeyAchievementsData } from 'api/queries/getKeyAchievementstableData.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { TIME_FILTERS } from 'components/widgets/KeyAchievements/keyAchievements.consts';

export type UseKeyAchievementsProvider = ReturnType<typeof useKeyAchievementsProvider>;

interface Props {
    componentKey: string;
    widgetId?: string;
    globalFilterData: any;
    localFilters: any;
    table: UseBaseTable['table'];
    compareVersionId: number | undefined;
    latestVersionId: number | undefined;
}

export const useKeyAchievementsProvider = ({
    widgetId,
    globalFilterData,
    localFilters,
    compareVersionId,
    latestVersionId,
    componentKey,
}: Props) => {
    const { isGenerativeAi, reportId } = usePinboardWidgetWrapperContext();
    const { getQueryKey } = useQuerystring();

    const [tableTimeFilterUrl] = useQueryState('tableTimeFilter');

    const activityTypeFilterUrlValue = useMemo(() => {
        return getQueryKey({ key: FilterNames.activity_type });
    }, [getQueryKey]);

    const isInPinboard = Boolean(widgetId);
    const source = isInPinboard
        ? localFilters
        : { ...globalFilterData, timePeriod: tableTimeFilterUrl, activityType: activityTypeFilterUrlValue };

    const normalizeActivityType = (raw: unknown): string[] => {
        if (raw == null) return [];

        if (Array.isArray(raw)) {
            return raw[0] === 'ALL' ? [] : raw;
        }

        if (typeof raw === 'string') {
            if (raw === 'ALL') return [];
            return raw.split(',');
        }

        return [];
    };

    const filters = useMemo(() => {
        return getFiltersData({
            [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
            [FilterNames.TAG_CARD_BOARD]: source?.tags
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.WBS_SEARCH]: source?.wbs,
            [FilterNames.activity_type]: normalizeActivityType(source?.activityType),
        });
    }, [source?.activityCodes, source?.activityType, source?.milestoneFragnet, source?.tags, source?.wbs]);

    const getKeyAchievementsDataPayload = {
        compareVersionId: compareVersionId,
        latestVersionId: latestVersionId,
        timePeriod: source?.timePeriod,
        filters: filters,
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId, reportId),
    };

    const getKeyAchievementsData = useQueryGetKeyAchievementsData(getKeyAchievementsDataPayload);

    // create pill data for PinboardWidgetPills component
    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        tagsExclude: source?.tags?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        wbs: source?.wbs,
        activityType: source?.activityType?.split(','),
        timePeriod: TIME_FILTERS.find((item) => item.value === source?.timePeriod)?.label,
    };

    const { pillsList: pills } = useGlobalFiltersHook({ widgetId, source: pillsData, latestVersionId });

    return {
        ui: {
            state: {
                activityCodes: source?.activityCodes,
                milestoneFragnet: source?.milestoneFragnet,
                tags: source?.tags,
                wbs: source?.wbs,
                activityType: source?.activityType,
                timePeriod: source?.timePeriod,
            },
            isInPinboard,
            pills,
        },
        api: {
            getKeyAchievementsData: {
                instance: getKeyAchievementsData,
                payload: getKeyAchievementsDataPayload,
            },
        },
    };
};
